import React, { createContext, useContext } from "react";
import { Link } from "gatsby";

import strings from "./strings";
import {
  defaultLocale,
  locales,
  localeNames,
  localeFromPath,
  stripLocale,
  localizePath,
} from "./config";

export { defaultLocale, locales, localeNames, localeFromPath, stripLocale, localizePath };

const LocaleContext = createContext(defaultLocale);

/**
 * Wraps every page (see gatsby-browser.js / gatsby-ssr.js). The locale comes
 * from the URL rather than the page context, so the 404 page — which Gatsby
 * serves for every unknown path — still speaks the language of the URL the
 * reader typed.
 */
export function LocaleProvider({ pathname, children }) {
  return (
    <LocaleContext.Provider value={localeFromPath(pathname)}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const locale = useContext(LocaleContext);
  return {
    locale,
    t: strings[locale] || strings[defaultLocale],
    localize: (path) => localizePath(path, locale),
  };
}

/** Every language version of a page, for hreflang links and the switcher. */
export function localeAlternates(basePath) {
  return locales.map((lang) => ({ lang, path: localizePath(basePath, lang) }));
}

/**
 * Dates are stored as calendar days ("2026-03-21"), which `new Date` reads as
 * UTC midnight — formatting in UTC keeps them from slipping back a day for
 * readers west of Greenwich.
 */
export function formatDate(value, locale, style = "long") {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const options =
    style === "short"
      ? { month: "short", day: "numeric", year: "numeric" }
      : { month: "long", day: "numeric", year: "numeric" };
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: "UTC" }).format(
    date
  );
}

/** A Gatsby Link whose `to` is written unprefixed and localized here. */
export function LocalizedLink({ to, ...props }) {
  const { localize } = useLocale();
  return <Link to={localize(to)} {...props} />;
}
